import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { s3ObjectPublicUrl } from '../../../utils/s3PublicUrl';

export type HoleRect = { left: number; top: number; width: number; height: number };

/** Visible rect when the image is shown with `object-fit: cover` in a frame of aspect frameW:frameH (center crop). */
export function computeAspectCoverHole(
  naturalW: number,
  naturalH: number,
  dispW: number,
  dispH: number,
  frameW: number,
  frameH: number,
): HoleRect | null {
  if (naturalW <= 0 || naturalH <= 0 || dispW <= 0 || dispH <= 0) return null;
  const rc = frameW / frameH;
  const ri = naturalW / naturalH;
  const s = Math.min(dispW / naturalW, dispH / naturalH);
  const contentW = s * naturalW;
  const contentH = s * naturalH;
  const offsetX = (dispW - contentW) / 2;
  const offsetY = (dispH - contentH) / 2;

  let rx: number;
  let ry: number;
  let rw: number;
  let rh: number;
  if (ri >= rc) {
    rw = naturalH * rc;
    rh = naturalH;
    rx = (naturalW - rw) / 2;
    ry = 0;
  } else {
    rw = naturalW;
    rh = naturalW / rc;
    rx = 0;
    ry = (naturalH - rh) / 2;
  }

  return {
    left: offsetX + rx * s,
    top: offsetY + ry * s,
    width: rw * s,
    height: rh * s,
  };
}

export type BookImageAspectCropPreviewProps = {
  file: File | null;
  storageKey: string;
  imageSx?: SxProps<Theme>;
  frameWidthPx: number;
  frameHeightPx: number;
  caption: string;
};

/**
 * Image preview with dimmed overlay outside the region that would fill a fixed
 * frame aspect using centered `object-fit: cover`.
 */
export default function BookImageAspectCropPreview({
  file,
  storageKey,
  imageSx,
  frameWidthPx,
  frameHeightPx,
  caption,
}: BookImageAspectCropPreviewProps) {
  const theme = useTheme();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [hole, setHole] = useState<HoleRect | null>(null);

  useEffect(() => {
    if (!file) {
      setBlobUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setBlobUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const src = blobUrl ?? s3ObjectPublicUrl(storageKey.trim() || null);

  useEffect(() => {
    setNatural(null);
    setHole(null);
  }, [src]);

  const updateHole = useCallback(() => {
    const img = imgRef.current;
    const wrap = wrapRef.current;
    if (!img || !wrap || !natural || natural.w <= 0 || natural.h <= 0) {
      setHole(null);
      return;
    }
    const dispW = img.clientWidth;
    const dispH = img.clientHeight;
    const next = computeAspectCoverHole(
      natural.w,
      natural.h,
      dispW,
      dispH,
      frameWidthPx,
      frameHeightPx,
    );
    setHole(next);
  }, [natural, frameWidthPx, frameHeightPx]);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !src) return;
    const ro = new ResizeObserver(() => {
      updateHole();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [src, updateHole]);

  useLayoutEffect(() => {
    updateHole();
  }, [natural, updateHole]);

  if (!src) return null;

  const shade = alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.62 : 0.48);

  return (
    <Box sx={{ width: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box
        ref={wrapRef}
        sx={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          lineHeight: 0,
          // Clip the hole’s huge box-shadow so dimming never spills onto siblings (buttons, headings, etc.).
          overflow: 'hidden',
          isolation: 'isolate',
        }}
      >
        <Box
          component="img"
          ref={imgRef}
          src={src}
          alt=""
          onLoad={(e) => {
            const el = e.currentTarget;
            setNatural({ w: el.naturalWidth, h: el.naturalHeight });
          }}
          sx={{
            maxWidth: 1,
            maxHeight: 160,
            objectFit: 'contain',
            display: 'block',
            ...imageSx,
          }}
        />
        {hole && hole.width > 0 && hole.height > 0 ? (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: hole.left,
              top: hole.top,
              width: hole.width,
              height: hole.height,
              boxShadow: `0 0 0 ${9999}px ${shade}`,
              pointerEvents: 'none',
              outline: (t) => `2px solid ${t.palette.primary.main}`,
              outlineOffset: '-1px',
            }}
          />
        ) : null}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 520 }}>
        {caption}
      </Typography>
    </Box>
  );
}
